import {
  AssetVizController,
  AssetVizHeatmapDisplayState,
  AssetVizSceneConfig,
} from 'features/assetViz'
import { ASSET_VIZ_DEFAULT_CONFIG } from 'pages/reporting/shared/constants'
import { useRef, useState } from 'react'

interface AssetVizStateResult {
  indicationsVizRef: React.RefObject<AssetVizController | null>
  drawingVizRef: React.RefObject<AssetVizController | null>
  baseSceneConfig: AssetVizSceneConfig
  heatmapSceneConfig: AssetVizSceneConfig
  noHeatmapSceneConfig: AssetVizSceneConfig
  aspectRatioRef: React.RefObject<number>
}

/**
 * Hook to manage AssetViz visualization state and configurations
 *
 * Provides:
 * - Refs for indications and drawing visualizations
 * - Scene configurations for different visualization modes
 * - Aspect ratio tracking
 *
 * @returns {AssetVizStateResult} Object containing refs and scene configurations
 */
export const useAssetVizState = (): AssetVizStateResult => {
  const indicationsVizRef = useRef<AssetVizController>(null)
  const drawingVizRef = useRef<AssetVizController>(null)
  const aspectRatioRef = useRef<number>(-1)

  // Base configuration with aspect ratio override
  const [baseSceneConfig] = useState<AssetVizSceneConfig>(() => ({
    ...ASSET_VIZ_DEFAULT_CONFIG,
    show3D: false,
    overrideCameraAspectRatio: aspectRatioRef,
    invertColors: true,
  }))

  // Configuration for heatmap visualization
  const [heatmapSceneConfig] = useState<AssetVizSceneConfig>(() => ({
    ...baseSceneConfig,
    heatmapDisplayState: AssetVizHeatmapDisplayState.Sharp,
  }))

  // Configuration for visualization without heatmap
  const [noHeatmapSceneConfig] = useState<AssetVizSceneConfig>(() => ({
    ...baseSceneConfig,
    heatmapDisplayState: AssetVizHeatmapDisplayState.Off,
  }))

  return {
    indicationsVizRef,
    drawingVizRef,
    baseSceneConfig,
    heatmapSceneConfig,
    noHeatmapSceneConfig,
    aspectRatioRef,
  }
}
